/**
 * BulkActionToolbar - Floating toolbar for bulk actions
 * Appears at bottom of screen when items are selected
 */

import { ReactNode, useState } from 'react';

export interface BulkActionButton {
  label: string;
  icon?: ReactNode;
  onClick: () => void | Promise<void>;
  variant?: 'primary' | 'secondary' | 'danger';
  requireConfirmation?: boolean;
  confirmMessage?: string;
  disabled?: boolean;
}

export interface BulkActionToolbarProps {
  selectedCount: number;
  onClearSelection: () => void;
  actions: BulkActionButton[];
}

/**
 * Floating toolbar for bulk actions
 */
export default function BulkActionToolbar({
  selectedCount,
  onClearSelection,
  actions,
}: BulkActionToolbarProps): JSX.Element | null {
  const [confirmingAction, setConfirmingAction] = useState<string | null>(null);
  const [executing, setExecuting] = useState(false);

  if (selectedCount === 0) {
    return null;
  }

  async function handleActionClick(action: BulkActionButton): Promise<void> {
    if (action.disabled) return;

    if (action.requireConfirmation) {
      setConfirmingAction(action.label);
      return;
    }

    try {
      setExecuting(true);
      await action.onClick();
    } catch (error) {
      console.error('Bulk action error:', error);
    } finally {
      setExecuting(false);
    }
  }

  async function handleConfirm(action: BulkActionButton): Promise<void> {
    setConfirmingAction(null);

    try {
      setExecuting(true);
      await action.onClick();
    } catch (error) {
      console.error('Bulk action error:', error);
    } finally {
      setExecuting(false);
    }
  }

  function handleCancel(): void {
    setConfirmingAction(null);
  }

  function getButtonClass(variant?: string): string {
    const baseClass = 'px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2';

    switch (variant) {
      case 'danger':
        return `${baseClass} bg-red-600 hover:bg-red-700 text-white`;
      case 'secondary':
        return `${baseClass} bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white`;
      case 'primary':
      default:
        return `${baseClass} bg-primary-600 hover:bg-primary-700 text-white`;
    }
  }

  const confirmingActionData = actions.find((a) => a.label === confirmingAction);

  return (
    <>
      {/* Main toolbar */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 animate-slide-up">
        <div className="bg-white dark:bg-gray-800 shadow-2xl rounded-lg p-4 border border-gray-200 dark:border-gray-700 min-w-[400px]">
          <div className="flex items-center gap-4">
            {/* Selection count */}
            <span className="font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
              {selectedCount} selected
            </span>

            {/* Divider */}
            <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              {actions.map((action) => (
                <button
                  key={action.label}
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled || executing}
                  className={getButtonClass(action.variant)}
                  title={action.label}
                >
                  {action.icon}
                  <span>{action.label}</span>
                </button>
              ))}
            </div>

            {/* Clear selection button */}
            <button
              onClick={onClearSelection}
              disabled={executing}
              className="ml-auto px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors disabled:opacity-50"
              title="Clear selection"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation modal */}
      {confirmingAction && confirmingActionData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-gray-100">
              Confirm Action
            </h3>

            <p className="text-gray-700 dark:text-gray-300 mb-6">
              {confirmingActionData.confirmMessage ||
                `Are you sure you want to ${confirmingAction.toLowerCase()} ${selectedCount} items?`}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancel}
                disabled={executing}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleConfirm(confirmingActionData)}
                disabled={executing}
                className={getButtonClass(confirmingActionData.variant)}
              >
                {executing ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
